/**
 * @file Constants — reviewStatus.
 * Submission reviewStatus values from the API (review_status_v2).
 * Do not send legacy enum strings to the backend.
 */

export const REVIEW_STATUS = {
  PENDING: "PENDING",
  FLAGGED_BY_ADMIN: "FLAGGED_BY_ADMIN",
  FLAGGED_ASSIGNED_TO_CONTRIBUTOR: "FLAGGED_ASSIGNED_TO_CONTRIBUTOR",
  REVISED: "REVISED",
  FLAGGED_ASSIGNED_TO_EXPERT: "FLAGGED_ASSIGNED_TO_EXPERT",
  ASSIGNED_TO_EXPERT: "ASSIGNED_TO_EXPERT",
  FLAGGED_BY_EXPERT: "FLAGGED_BY_EXPERT",
  APPROVED: "APPROVED",
} as const;

/** Type definition — Review Status. */
export type ReviewStatus = (typeof REVIEW_STATUS)[keyof typeof REVIEW_STATUS];

/** Admin list / material overview filter dropdown (new values only). */
export const ADMIN_REVIEW_STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: REVIEW_STATUS.PENDING, label: "Pending" },
  { value: REVIEW_STATUS.FLAGGED_BY_ADMIN, label: "Flagged (admin only)" },
  { value: REVIEW_STATUS.FLAGGED_ASSIGNED_TO_CONTRIBUTOR, label: "Flagged — return to contributor" },
  { value: REVIEW_STATUS.REVISED, label: "Revised" },
  { value: REVIEW_STATUS.FLAGGED_ASSIGNED_TO_EXPERT, label: "Flagged — assigned to expert" },
  { value: REVIEW_STATUS.ASSIGNED_TO_EXPERT, label: "Assigned to expert" },
  { value: REVIEW_STATUS.FLAGGED_BY_EXPERT, label: "Flagged by expert" },
  { value: REVIEW_STATUS.APPROVED, label: "Approved" },
];

/** Admin submissions table filter — omits “Assigned to expert” (expert queue). */
export const ADMIN_SUBMISSION_LIST_REVIEW_STATUS_FILTER_OPTIONS =
  ADMIN_REVIEW_STATUS_FILTER_OPTIONS.filter(
    (o) => o.value !== REVIEW_STATUS.ASSIGNED_TO_EXPERT
  );

/** Admin material overview filter — omits “Flagged by expert”. */
export const ADMIN_MATERIAL_OVERVIEW_REVIEW_STATUS_FILTER_OPTIONS =
  ADMIN_REVIEW_STATUS_FILTER_OPTIONS.filter(
    (o) => o.value !== REVIEW_STATUS.FLAGGED_BY_EXPERT
  );

/** Stable order for admin review-status filters (matches `ADMIN_REVIEW_STATUS_FILTER_OPTIONS`). */
const ADMIN_REVIEW_STATUS_FILTER_ORDER_INDEX = new Map(
  ADMIN_REVIEW_STATUS_FILTER_OPTIONS.map((o, i) => [o.value.toUpperCase(), i])
);

/**
 * Sort admin review-status filter options: known statuses follow product order;
 * any extra/legacy values appear after, sorted by label.
 */
export function sortAdminReviewStatusFilterOptions(
  options: { value: string; label: string }[]
): { value: string; label: string }[] {
  const fallback = ADMIN_REVIEW_STATUS_FILTER_OPTIONS.length;
  return [...options].sort((a, b) => {
    const ia = ADMIN_REVIEW_STATUS_FILTER_ORDER_INDEX.get(a.value.toUpperCase()) ?? fallback;
    const ib = ADMIN_REVIEW_STATUS_FILTER_ORDER_INDEX.get(b.value.toUpperCase()) ?? fallback;
    if (ia !== ib) return ia - ib;
    return a.label.localeCompare(b.label);
  });
}

/** Map legacy API values to new labels for display only (during migration). */
const LEGACY_DISPLAY: Record<string, string> = {
  FLAGGED: REVIEW_STATUS.FLAGGED_BY_ADMIN,
  RESUBMITTED: REVIEW_STATUS.REVISED,
  APPROVED_ADMIN: REVIEW_STATUS.APPROVED,
  ASSIGNED_EXPERT: REVIEW_STATUS.ASSIGNED_TO_EXPERT,
  REVIEWED: REVIEW_STATUS.APPROVED,
  REJECTED: REVIEW_STATUS.FLAGGED_BY_EXPERT,
};

/** Exported function — normalize Review Status. */
export function normalizeReviewStatus(raw: string | null | undefined): string {
  const s = String(raw ?? "").trim().toUpperCase();
  return LEGACY_DISPLAY[s] ?? s;
}

const LABELS: Record<string, string> = {
  [REVIEW_STATUS.PENDING]: "Pending",
  [REVIEW_STATUS.FLAGGED_BY_ADMIN]: "Flagged (admin)",
  [REVIEW_STATUS.FLAGGED_ASSIGNED_TO_CONTRIBUTOR]: "Flagged — contributor",
  [REVIEW_STATUS.REVISED]: "Revised",
  [REVIEW_STATUS.FLAGGED_ASSIGNED_TO_EXPERT]: "Flagged — expert",
  [REVIEW_STATUS.ASSIGNED_TO_EXPERT]: "Assigned to expert",
  [REVIEW_STATUS.FLAGGED_BY_EXPERT]: "Flagged by expert",
  [REVIEW_STATUS.APPROVED]: "Approved",
};

/** Exported function — format Review Status Label. */
export function formatReviewStatusLabel(status: string | null | undefined): string {
  const n = normalizeReviewStatus(status);
  return LABELS[n] ?? n.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/** CSS class suffix: `status-badge-${reviewStatusBadgeSuffix(status)}` */
export function reviewStatusBadgeSuffix(status: string | null | undefined): string {
  return normalizeReviewStatus(status).toLowerCase().replace(/_/g, "-");
}

/** Dashboard: submissions that likely need admin attention. */
export function isAdminQueueReviewStatus(status: string | null | undefined): boolean {
  const s = normalizeReviewStatus(status);
  return (
    s === REVIEW_STATUS.PENDING ||
    s === REVIEW_STATUS.FLAGGED_BY_ADMIN ||
    s === REVIEW_STATUS.FLAGGED_ASSIGNED_TO_CONTRIBUTOR ||
    s === REVIEW_STATUS.REVISED ||
    s === REVIEW_STATUS.FLAGGED_ASSIGNED_TO_EXPERT
  );
}

/** Expert active queue (strict). */
export function isExpertActiveQueueStatus(status: string | null | undefined): boolean {
  const s = normalizeReviewStatus(status);
  return s === REVIEW_STATUS.ASSIGNED_TO_EXPERT || s === REVIEW_STATUS.FLAGGED_ASSIGNED_TO_EXPERT;
}

/** Admin may mark dataset approved (not already approved; contributor must act first on return-to-contributor). */
export function canAdminApproveDataset(status: string | null | undefined): boolean {
  const s = normalizeReviewStatus(status);
  if (s === REVIEW_STATUS.APPROVED) return false;
  if (s === REVIEW_STATUS.FLAGGED_ASSIGNED_TO_CONTRIBUTOR) return false;
  return true;
}

/** Type definition — Expert Review Submit Status. */
export type ExpertReviewSubmitStatus = "APPROVED" | "FLAGGED_BY_EXPERT" | "PENDING";
